import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { OneValueMeasurement } from "~/api/lastMeasurements";
import { BloodGlucose } from "~/icons";
import { Button } from "~/ui";
import { formatBackendDate } from "~/utils";
import { HealthDetails } from "..";
import { BloodGlucoseInputModal } from "./BloodGlucoseInputModal";

interface BloodGlucoseCardProps {
  measurement: OneValueMeasurement | null;
  patientId: number;
}

export const BloodGlucoseCard = ({
  measurement,
  patientId,
}: BloodGlucoseCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const val = measurement ? measurement.value.toString() : t("health.no_data");

  const handleViewMore = () => {
    void navigate(`/airmen/${patientId}/blood-glucose`);
  };

  const handleAddMeasurement = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    setIsModalOpen(true);
  };

  // Collapsed card view
  return (
    <>
      <HealthDetails.Card
        onClick={handleViewMore}
        className="cursor-pointer transition-shadow duration-200 hover:shadow-lg"
      >
        <HealthDetails.CardHeader className="justify-between">
          <div className="flex items-center gap-2">
            <BloodGlucose className="text-primary-40 dark:text-primary-80" />
            <HealthDetails.CardTitle>
              {t("health.blood_glucose")}
            </HealthDetails.CardTitle>
          </div>

          <Button
            text="Log data"
            color="primary"
            onClick={handleAddMeasurement}
            className="px-3 py-0.5 [&_span]:text-sm"
          />
        </HealthDetails.CardHeader>
        <HealthDetails.CardBody>
          <HealthDetails.CardResult>{val}</HealthDetails.CardResult>
          <HealthDetails.CardDetails>
            <HealthDetails.CardUnit>{t("health.mg_dl")}</HealthDetails.CardUnit>
            {measurement && (
              <HealthDetails.CardDate>
                {formatBackendDate(measurement.date)}
              </HealthDetails.CardDate>
            )}
          </HealthDetails.CardDetails>
          <div className="flex flex-1 items-center justify-end">
            <button className="text-sm text-primary-40 transition-colors hover:text-primary-30 dark:text-primary-80 dark:hover:text-primary-90">
              {t("general.view_more")}
            </button>
          </div>
        </HealthDetails.CardBody>
      </HealthDetails.Card>

      <BloodGlucoseInputModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        patientId={patientId}
      />
    </>
  );
};
